import { Link } from "react-router-dom";
import { LegalDisclaimer } from "../components/LegalDisclaimer";
import {
  ArrowLeft, CheckCircle, ArrowSquareOut, Scales, ShieldCheck,
  Users, FileText, Warning, Book,
} from "@phosphor-icons/react";

/**
 * BylawsTemplates — Formation Toolkit Module 03
 *
 * Curated bylaws and governance resource page for SC pre-501(c)(3) founders.
 * NO invented legal language. Every reference cites its primary source (IRS or SC statute).
 * Templates linked are IRS-authored; SC customization guidance layered on top.
 *
 * Discipline: this module curates and orients. It does not generate legal text.
 */

const BYLAWS_ELEMENTS = [
  {
    title: "Name of the organization",
    detail: "Must match the name on your SC Articles of Incorporation exactly.",
  },
  {
    title: "Purpose statement",
    detail: "Must align with your Form 1023-EZ mission statement and satisfy IRS charitable-purpose requirements (Section 501(c)(3)).",
  },
  {
    title: "Membership provisions (or lack thereof)",
    detail: "Most small SC 501(c)(3)s are 'non-membership' corporations governed by the board. If you choose to have members, define eligibility, rights, and voting.",
  },
  {
    title: "Board of Directors — composition, terms, and qualifications",
    detail: "Number of directors (SC Nonprofit Corporation Act requires a minimum), term length, staggered vs. concurrent terms, whether officers must be directors.",
  },
  {
    title: "Officer positions and duties",
    detail: "At minimum: President, Secretary, Treasurer. Define what each officer does and how they're elected.",
  },
  {
    title: "Board meeting procedures",
    detail: "Frequency (annual minimum), notice requirements, quorum, voting rules, remote/electronic meeting provisions.",
  },
  {
    title: "Committee structure",
    detail: "Executive committee, finance committee, and any standing committees. Committee authority relative to the full board.",
  },
  {
    title: "Fiscal year",
    detail: "Most SC nonprofits use the calendar year (Jan 1 – Dec 31). Must match the fiscal year on your Form 1023-EZ.",
  },
  {
    title: "Indemnification of directors and officers",
    detail: "Standard language protecting board members from personal liability for good-faith decisions made in their board role.",
  },
  {
    title: "Conflict of Interest policy (referenced or attached)",
    detail: "IRS Form 1023 asks whether the organization has adopted one. Highly recommended to attach the IRS sample policy as an appendix to your bylaws.",
  },
  {
    title: "Amendment procedures",
    detail: "How future amendments to the bylaws are proposed and adopted (typically 2/3 board vote at a duly noticed meeting).",
  },
  {
    title: "Dissolution clause",
    detail: "REQUIRED for 501(c)(3) status. Must state that upon dissolution, assets transfer to another 501(c)(3) organization or to a government entity for public purposes. This language is verified against IRS Publication 557 requirements.",
  },
];

const SC_CUSTOMIZATION = [
  {
    field: "Registered Agent",
    guidance: "Must be a South Carolina resident or SC-authorized business entity with a physical SC address (not a P.O. box). This is the person/entity legally authorized to receive service of process. Verify with SC Secretary of State.",
  },
  {
    field: "Principal Office Address",
    guidance: "SC address of the organization's main office. This can be a home address for early-stage nonprofits.",
  },
  {
    field: "Minimum Number of Directors",
    guidance: "The SC Nonprofit Corporation Act requires a minimum board size. Most SC nonprofits set 3 as the minimum floor in their bylaws; many use 5–7 for stronger governance and IRS credibility.",
  },
  {
    field: "Director Composition Requirement",
    guidance: "The IRS prefers a majority of unrelated directors (not related by blood, marriage, or business partnership) to establish independence for 501(c)(3) purposes. State this in the bylaws.",
  },
  {
    field: "Annual Meeting Requirement",
    guidance: "SC statute requires at least one annual meeting of the board. Specify a default month (e.g., 'first quarter of each fiscal year') to force calendar discipline.",
  },
  {
    field: "Fiscal Year Election",
    guidance: "State clearly whether the fiscal year is calendar-year or a different 12-month period. This must match your Form 1023-EZ and your future Form 990 filings.",
  },
  {
    field: "SC Annual Report Filing Reference",
    guidance: "SC nonprofits file periodic reports with the Secretary of State. Reference the organization's obligation to comply with SC filing requirements in the bylaws (protects the org if a director ever forgets).",
  },
  {
    field: "Charitable Solicitation Compliance",
    guidance: "If your SC nonprofit will solicit donations, you must register with the SC Secretary of State's Public Charities Division under the SC Solicitation of Charitable Funds Act. Reference this obligation in your bylaws.",
  },
];

const GOVERNANCE_POLICIES = [
  {
    title: "Conflict of Interest Policy",
    description: "REQUIRED by IRS Form 1023 questions. The IRS provides a sample policy in Appendix A of the Form 1023 Instructions (dated but authoritative).",
    source: "IRS Form 1023 Instructions, Appendix A",
    link: "https://www.irs.gov/pub/irs-pdf/i1023.pdf",
    linkLabel: "Download IRS Form 1023 Instructions (PDF)",
  },
  {
    title: "Document Retention & Destruction Policy",
    description: "IRS Form 990 asks whether the organization has one. Establishes what records must be kept and for how long (typically: tax records 7 years, corporate records permanently, personnel records varies).",
    source: "IRS Publication 4221-PC — Compliance Guide for 501(c)(3) Public Charities",
    link: "https://www.irs.gov/pub/irs-pdf/p4221pc.pdf",
    linkLabel: "Download IRS Publication 4221-PC (PDF)",
  },
  {
    title: "Whistleblower Protection Policy",
    description: "IRS Form 990 asks whether the organization has one. Protects staff, volunteers, and board members from retaliation for reporting suspected illegal, unethical, or improper conduct.",
    source: "IRS Governance and Related Topics guidance",
    link: "https://www.irs.gov/pub/irs-tege/governance_practices.pdf",
    linkLabel: "Read IRS Governance Practices guidance (PDF)",
  },
  {
    title: "Compensation Review Procedures",
    description: "For SC nonprofits that will pay any executive compensation. Establishes the process for the board to review and approve executive compensation using comparable data (IRS 'rebuttable presumption' safe harbor).",
    source: "IRS Publication 4221-PC + Treasury Regulation §53.4958-6",
    link: "https://www.irs.gov/pub/irs-pdf/p4221pc.pdf",
    linkLabel: "See IRS 4221-PC compensation section",
  },
];

const BYLAWS_OUTLINE = `ARTICLE I — NAME AND PURPOSE
  Section 1.1  Name
  Section 1.2  Purpose (charitable, educational, religious, etc.)
  Section 1.3  Nonprofit Status (501(c)(3) intent)

ARTICLE II — OFFICES
  Section 2.1  Principal Office
  Section 2.2  Registered Agent and Registered Office
  Section 2.3  Other Offices

ARTICLE III — MEMBERSHIP
  Section 3.1  Membership (or "This corporation shall have no members")

ARTICLE IV — BOARD OF DIRECTORS
  Section 4.1  Powers
  Section 4.2  Number and Qualifications
  Section 4.3  Term of Office
  Section 4.4  Vacancies and Removal
  Section 4.5  Regular and Special Meetings
  Section 4.6  Notice of Meetings
  Section 4.7  Quorum and Voting
  Section 4.8  Action Without a Meeting
  Section 4.9  Compensation of Directors

ARTICLE V — OFFICERS
  Section 5.1  Officer Positions (President, Secretary, Treasurer, others)
  Section 5.2  Election and Term
  Section 5.3  Duties of Each Officer
  Section 5.4  Vacancies and Removal

ARTICLE VI — COMMITTEES
  Section 6.1  Executive Committee
  Section 6.2  Standing Committees
  Section 6.3  Ad Hoc Committees

ARTICLE VII — FISCAL YEAR AND RECORDS
  Section 7.1  Fiscal Year
  Section 7.2  Books and Records
  Section 7.3  Annual Report

ARTICLE VIII — CONFLICT OF INTEREST
  Section 8.1  Adoption of Conflict of Interest Policy (attached as Appendix A)

ARTICLE IX — INDEMNIFICATION
  Section 9.1  Indemnification of Directors and Officers

ARTICLE X — AMENDMENTS
  Section 10.1  Amendment Procedures

ARTICLE XI — DISSOLUTION
  Section 11.1  Dissolution Clause
    (Assets shall be distributed to another 501(c)(3) organization
     or to a governmental unit for a public purpose, per IRS
     Publication 557 requirements.)`;

const BylawsTemplates = () => {
  return (
    <div className="min-h-screen bg-black text-white" data-testid="bylaws-templates-page">
      <div className="max-w-4xl mx-auto px-6 py-10">
        {/* Back link */}
        <Link
          to="/formation"
          className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-emerald-400 transition-colors mb-8"
          data-testid="back-to-toolkit-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Formation Toolkit
        </Link>

        {/* Header */}
        <div className="mb-10">
          <div className="text-xs uppercase tracking-widest text-emerald-400 font-semibold mb-3">
            Formation Toolkit · Module 03
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            Bylaws & Governance Templates
          </h1>
          <p className="text-lg text-zinc-400 leading-relaxed">
            A curated resource — not a legal document generator. Every element below
            cites its primary source (IRS or South Carolina statute). Use this to
            build a bylaws document that satisfies IRS Form 1023 review and SC
            nonprofit corporation law.
          </p>
        </div>

        <LegalDisclaimer variant="banner" />

        {/* Section 1: What are bylaws & why they matter */}
        <section className="mb-14" data-testid="section-what-are-bylaws">
          <h2 className="text-2xl font-bold text-emerald-400 mb-4 flex items-center gap-3">
            <Book className="w-6 h-6" />
            What bylaws are (and why they matter)
          </h2>
          <div className="space-y-4 text-zinc-300 leading-relaxed">
            <p>
              Bylaws are the internal rulebook of your nonprofit — how your board
              operates, how decisions get made, how officers are elected, and how
              the organization protects itself. Unlike your Articles of Incorporation
              (filed with the SC Secretary of State), bylaws are <b>not filed with the
              state</b>. They live inside your organization as a permanent record.
            </p>
            <p>
              Bylaws are also part of <b>what the IRS reviews</b> when it evaluates
              your Form 1023 or 1023-EZ application. Weak or missing bylaws can
              slow — or block — your 501(c)(3) determination.
            </p>
            <p className="text-emerald-300/90">
              This module gives you the IRS-required elements, the SC-specific
              customization checklist, and links to the exact primary-source
              governance policy templates the IRS publishes. It does not write
              your bylaws for you. It maps you to the authoritative sources so
              you can build them correctly the first time.
            </p>
          </div>
        </section>

        {/* Section 2: IRS-required bylaws elements */}
        <section className="mb-14" data-testid="section-irs-elements">
          <h2 className="text-2xl font-bold text-emerald-400 mb-2 flex items-center gap-3">
            <FileText className="w-6 h-6" />
            The 12 elements the IRS looks for
          </h2>
          <p className="text-sm text-zinc-500 mb-6">
            Sourced from IRS Form 1023 Instructions + IRS Publication 557 requirements.
          </p>
          <div className="space-y-4">
            {BYLAWS_ELEMENTS.map((el, i) => (
              <div
                key={i}
                className="flex items-start gap-4 p-4 rounded-lg border border-zinc-900 bg-zinc-950/50"
                data-testid={`bylaws-element-${i + 1}`}
              >
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 text-sm font-bold">
                  {i + 1}
                </div>
                <div>
                  <div className="font-semibold text-white mb-1">{el.title}</div>
                  <div className="text-sm text-zinc-400 leading-relaxed">{el.detail}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Section 3: SC customization checklist */}
        <section className="mb-14" data-testid="section-sc-customization">
          <h2 className="text-2xl font-bold text-emerald-400 mb-2 flex items-center gap-3">
            <Scales className="w-6 h-6" />
            South Carolina customization checklist
          </h2>
          <p className="text-sm text-zinc-500 mb-6">
            Sourced from the SC Nonprofit Corporation Act (SC Code Title 33, Chapter 31)
            and SC Solicitation of Charitable Funds Act. Verify with the{" "}
            <a
              href="https://sos.sc.gov/business-filings/nonprofits"
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-400 hover:underline"
            >
              SC Secretary of State&apos;s Business Filings page
            </a>{" "}
            or a licensed SC attorney before filing.
          </p>
          <div className="space-y-3">
            {SC_CUSTOMIZATION.map((item, i) => (
              <div
                key={i}
                className="p-4 rounded-lg border border-zinc-900 bg-zinc-950/50"
                data-testid={`sc-customization-${i + 1}`}
              >
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-white mb-1">{item.field}</div>
                    <div className="text-sm text-zinc-400 leading-relaxed">
                      {item.guidance}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Section 4: IRS governance policy templates */}
        <section className="mb-14" data-testid="section-governance-policies">
          <h2 className="text-2xl font-bold text-emerald-400 mb-2 flex items-center gap-3">
            <ShieldCheck className="w-6 h-6" />
            IRS-published governance policy templates
          </h2>
          <p className="text-sm text-zinc-500 mb-6">
            The IRS itself publishes the following sample policies. Download and
            adapt each to your organization. These are not FlashGrants documents —
            they are the primary sources.
          </p>
          <div className="space-y-4">
            {GOVERNANCE_POLICIES.map((policy, i) => (
              <div
                key={i}
                className="p-5 rounded-lg border border-zinc-800 bg-zinc-950"
                data-testid={`governance-policy-${i + 1}`}
              >
                <div className="font-semibold text-white text-lg mb-2">
                  {policy.title}
                </div>
                <div className="text-sm text-zinc-400 leading-relaxed mb-3">
                  {policy.description}
                </div>
                <div className="text-xs text-zinc-500 mb-3">
                  <b>Source:</b> {policy.source}
                </div>
                <a
                  href={policy.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 text-sm font-medium transition-colors"
                  data-testid={`governance-policy-link-${i + 1}`}
                >
                  {policy.linkLabel}
                  <ArrowSquareOut className="w-4 h-4" />
                </a>
              </div>
            ))}
          </div>
        </section>

        {/* Section 5: Sample bylaws structure */}
        <section className="mb-14" data-testid="section-sample-outline">
          <h2 className="text-2xl font-bold text-emerald-400 mb-2 flex items-center gap-3">
            <Users className="w-6 h-6" />
            Sample bylaws outline (copy this structure)
          </h2>
          <p className="text-sm text-zinc-500 mb-6">
            A well-organized bylaws document typically follows this article
            structure. Copy this outline into your document, then fill in the
            substance for each section using the IRS-required elements above and
            your SC customization guidance.
          </p>
          <pre className="p-6 rounded-lg border border-zinc-800 bg-zinc-950 text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap font-mono overflow-x-auto">
{BYLAWS_OUTLINE}
          </pre>
        </section>

        {/* Section 6: When to hire an attorney */}
        <section className="mb-14" data-testid="section-when-attorney">
          <h2 className="text-2xl font-bold text-amber-400 mb-2 flex items-center gap-3">
            <Warning className="w-6 h-6" />
            When to hire an SC attorney (do not skip this)
          </h2>
          <p className="text-sm text-zinc-500 mb-6">
            Template-based bylaws cover ~80% of standard SC 501(c)(3) situations.
            For the following, consult a licensed SC attorney before finalizing:
          </p>
          <ul className="space-y-3 text-zinc-300">
            <li className="flex items-start gap-3">
              <span className="text-amber-400 mt-1">•</span>
              <span>
                <b>Membership organizations</b> — if your nonprofit will have
                voting members (churches, associations, member-based advocacy
                groups), the governance provisions get materially more complex.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-amber-400 mt-1">•</span>
              <span>
                <b>Compensation of family members or founders</b> — IRS scrutiny
                is intense here; get compensation policy language reviewed by counsel.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-amber-400 mt-1">•</span>
              <span>
                <b>Fiscal sponsor arrangements</b> — if another 501(c)(3) will
                fiscally sponsor your work, the bylaws should reflect that
                relationship&apos;s terms.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-amber-400 mt-1">•</span>
              <span>
                <b>Multi-state operations</b> — if your SC nonprofit will operate
                in other states, you&apos;ll need foreign-qualification analysis.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-amber-400 mt-1">•</span>
              <span>
                <b>Real property ownership</b> — nonprofits acquiring real estate
                need bylaws provisions on property authority and encumbrance limits.
              </span>
            </li>
          </ul>
          <div className="mt-6 p-4 rounded-lg border border-amber-500/30 bg-amber-500/5">
            <p className="text-sm text-amber-100/90 leading-relaxed">
              <b className="text-amber-300">SC-specific resources:</b>{" "}
              The{" "}
              <a
                href="https://www.scbar.org/lawyers/legal-resources/lawyer-referral-service/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-amber-300 hover:underline"
              >
                SC Bar Lawyer Referral Service
              </a>{" "}
              can connect you with SC nonprofit attorneys. Many offer initial
              consultations at reduced or no cost for nonprofit formation.
            </p>
          </div>
        </section>

        <LegalDisclaimer variant="footer" />
      </div>
    </div>
  );
};

export default BylawsTemplates;
